/**
 * Realm (Battle.net) connection: JSON over WebSocket, typed events, awaitable requests.
 */
import { type DataProfile, PROTOCOL_VERSION, type RealmEvent, type RealmRequest } from '@d2/protocol';
import { report } from './report';

/** The realm refused this client's game data or version; `message` is the realm's reason. */
export class RealmRefused extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

type Handler<E extends RealmEvent['t']> = (ev: Extract<RealmEvent, { t: E }>) => void;

export class RealmClient {
  private ws: WebSocket | null = null;
  private readonly handlers = new Map<string, Set<(ev: RealmEvent) => void>>();
  account = '';
  connected = false;
  ping = 0;
  private pingTimer = 0;

  constructor(readonly url: string) {}

  static defaultUrl(): string {
    const env = (import.meta as unknown as { env?: Record<string, string> }).env;
    if (env?.VITE_REALM_URL) return env.VITE_REALM_URL;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/realm`;
  }

  /**
   * Opens the connection and introduces the client (version, game data). Resolves with the
   * realm's welcome once the realm accepts the client (`ready`); rejects with `RealmRefused`
   * when it doesn't. `onOpen` runs once the socket is open and the version check begins.
   */
  connect(dataHash: string, data?: DataProfile, onOpen?: () => void): Promise<Extract<RealmEvent, { t: 'welcome' }>> {
    return new Promise((resolve, reject) => {
      let welcome: Extract<RealmEvent, { t: 'welcome' }> | null = null;
      let settled = false;
      const ws = new WebSocket(this.url);
      this.ws = ws;
      const timer = setTimeout(() => {
        ws.close();
        reject(new Error('Unable to connect to Battle.net.'));
      }, 8000);
      ws.onopen = () => {
        this.connected = true;
        onOpen?.();
        this.send({ t: 'hello', version: PROTOCOL_VERSION, dataHash, client: 'd2web', data });
      };
      ws.onmessage = (m) => {
        let ev: RealmEvent;
        try {
          ev = JSON.parse(String(m.data));
        } catch {
          return;
        }
        if (ev.t === 'welcome') welcome = ev;
        if (ev.t === 'ready' && welcome && !settled) {
          settled = true;
          clearTimeout(timer);
          resolve(welcome);
          this.pingTimer = window.setInterval(() => this.send({ t: 'ping', n: performance.now() }), 10_000);
        }
        if (ev.t === 'error' && ev.req === 'hello' && !settled) {
          settled = true;
          clearTimeout(timer);
          reject(new RealmRefused(ev.code, ev.msg));
          return;
        }
        if (ev.t === 'pong') this.ping = Math.round(performance.now() - ev.n);
        this.emit(ev);
      };
      ws.onerror = () => {
        clearTimeout(timer);
        if (!settled) reject(new Error('Unable to connect to Battle.net.'));
        settled = true;
      };
      ws.onclose = (e) => {
        // Closes the realm meant (1000, or 1001 on shutdown) are not worth a report.
        if (this.ws === ws && e.code !== 1000 && e.code !== 1001) report('Battle.net socket closed', `code ${e.code}${e.reason ? ` (${e.reason})` : ''}${e.wasClean ? '' : ', not clean'}`);
        this.connected = false;
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(new Error('Unable to connect to Battle.net.'));
        }
        clearInterval(this.pingTimer);
        this.emit({ t: 'error', code: 'serverBusy', msg: 'Your connection to Battle.net has been interrupted.' });
      };
    });
  }

  send(req: RealmRequest): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(req));
  }

  on<E extends RealmEvent['t']>(t: E, h: Handler<E>): () => void {
    let set = this.handlers.get(t);
    if (!set) {
      set = new Set();
      this.handlers.set(t, set);
    }
    const fn = h as (ev: RealmEvent) => void;
    set.add(fn);
    return () => set!.delete(fn);
  }

  private emit(ev: RealmEvent): void {
    for (const h of this.handlers.get(ev.t) ?? []) h(ev);
  }

  /** Sends a request and resolves with the first matching success event or rejects on error. */
  request<E extends RealmEvent['t']>(req: RealmRequest, success: E, timeoutMs = 10_000): Promise<Extract<RealmEvent, { t: E }>> {
    return new Promise((resolve, reject) => {
      const offs: (() => void)[] = [];
      const done = () => offs.forEach((o) => o());
      const timer = setTimeout(() => {
        done();
        reject(new Error('Battle.net did not respond.'));
      }, timeoutMs);
      offs.push(
        this.on(success, (ev) => {
          clearTimeout(timer);
          done();
          resolve(ev);
        }),
      );
      offs.push(
        this.on('error', (ev) => {
          if (ev.req && ev.req !== req.t) return;
          clearTimeout(timer);
          done();
          reject(new Error(ev.msg));
        }),
      );
      this.send(req);
    });
  }

  close(): void {
    this.ws?.close(1000); // normal closure (1005 "no status" otherwise)
    this.ws = null;
  }
}

/** Process-wide realm session (survives screen changes). */
export const session: {
  /** Battle.net (realm) or single player (in-browser host, local heroes). */
  mode: 'bnet' | 'single';
  realm: RealmClient | null;
  account: string;
  char: import('@d2/protocol').CharSummary | null;
  chars: import('@d2/protocol').CharSummary[];
  motd: string[];
  /** The lobby's "Battle.net News Updates", newest first. */
  news: import('@d2/protocol').NewsItem[];
  /** The realm's own name ("Athena Realm"), for the Current Realm box. */
  realmName: string;
  /** The realm runs a ladder season (its welcome said so). */
  ladder: boolean;
  /** The realm sends email: accounts may add an address and reset a forgotten password. */
  mail: boolean;
} = { mode: 'bnet', realm: null, account: '', char: null, chars: [], motd: [], news: [], realmName: '', ladder: false, mail: false };
